/**
 * PersonalizadorCartHandler
 * Gestiona lineas addon anidadas (items[n]) para bloques de personalizacion activos.
 * No sobreescribe handleSubmit; trabaja con inputs hidden en el formulario de producto.
 */
class PersonalizadorCartHandler {
  constructor() {
    this.init();
  }

  init() {
    const wrappers = document.querySelectorAll('.personalizador-wrapper');
    wrappers.forEach((wrapper) => this.bindWrapper(wrapper));
  }

  bindWrapper(wrapper) {
    if (!(wrapper instanceof HTMLElement)) return;
    if (wrapper.dataset.personalizadorBound === 'true') return;

    const form = this.resolveForm(wrapper);
    if (!(form instanceof HTMLFormElement)) return;

    const syncFn = () => this.sync(wrapper, form);

    wrapper.addEventListener('input', syncFn);
    wrapper.addEventListener('change', syncFn);
    wrapper.addEventListener('personalizador:sync', syncFn);
    wrapper.addEventListener('image-upload:selected', syncFn);
    wrapper.addEventListener('image-upload:success', syncFn);
    wrapper.addEventListener('image-upload:error', syncFn);
    wrapper.addEventListener('image-upload:clear', syncFn);

    form.addEventListener('input', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (target.matches('input[name="id"], input[name="quantity"]')) {
        syncFn();
      }
    });

    form.addEventListener('submit', syncFn);

    wrapper.dataset.personalizadorBound = 'true';
    syncFn();
  }

  resolveForm(wrapper) {
    const formId = wrapper.dataset.productFormId;
    if (formId) {
      const byId = document.getElementById(formId);
      if (byId instanceof HTMLFormElement) return byId;
      if (byId instanceof HTMLElement) {
        const nested = byId.querySelector('form[action*="/cart/add"]');
        if (nested instanceof HTMLFormElement) return nested;
      }
    }

    const section = wrapper.closest('.shopify-section');
    if (section) {
      const scoped = section.querySelector('form[action*="/cart/add"]');
      if (scoped instanceof HTMLFormElement) return scoped;
    }

    const fallback = document.querySelector('form[action*="/cart/add"]');
    return fallback instanceof HTMLFormElement ? fallback : null;
  }

  sync(wrapper, form) {
    this.clearAddonInputs(form);

    const mode = this.getMode(wrapper);
    const mainVariantId = this.getInputValue(form, 'input[name="id"]');
    const parentReference = this.buildParentReference(wrapper, mainVariantId);
    const quantityRaw = this.getInputValue(form, 'input[name="quantity"]');
    const quantity = Number.parseInt(quantityRaw || '1', 10);
    const safeQuantity = Number.isNaN(quantity) || quantity < 1 ? 1 : quantity;

    const blocks = Array.from(wrapper.querySelectorAll('.personalizador__block[data-personalizador-block-key]'));
    this.syncParentPropertyNames(blocks, mode);
    this.syncWrapperPropertyNames(wrapper, mode);

    if (mode !== 'custom') {
      this.toggleAddToCart(form, false);
      this.setError(wrapper, '');
      this.updateButtonPrice(wrapper, form);
      this.emitDebug(wrapper);
      return;
    }

    const missingRequired = blocks.find((block) => this.isRequiredAndEmpty(block));
    if (missingRequired) {
      const label = missingRequired.dataset.propertyLabel || 'campo obligatorio';
      this.toggleAddToCart(form, true);
      const requiredTemplate = wrapper.dataset.i18nErrorRequired || 'Completa el campo obligatorio: {label}.';
      this.setError(wrapper, requiredTemplate.replace('{label}', label));
      this.updateButtonPrice(wrapper, form);
      this.emitDebug(wrapper);
      return;
    }

    if (wrapper.dataset.nombreLogosMutex === 'true') {
      const nombreInput = wrapper.querySelector('.personalizador__block--nombre [data-personalizador-visible-property]');
      const logoCheckboxes = Array.from(wrapper.querySelectorAll('.personalizador__logo-checkbox'));
      const nombreActive = !!(nombreInput && nombreInput.value.trim().length > 0);
      const logosActive = logoCheckboxes.some((cb) => cb.checked);
      if (nombreActive && logosActive) {
        this.toggleAddToCart(form, true);
        this.setError(wrapper, wrapper.dataset.i18nMutexConflict || 'No puedes combinar nombre y logo en este producto.');
        this.updateButtonPrice(wrapper, form);
        this.emitDebug(wrapper);
        return;
      }
    }

    this.toggleAddToCart(form, false);
    this.setError(wrapper, '');

    if (!mainVariantId) {
      this.updateButtonPrice(wrapper, form);
      this.emitDebug(wrapper);
      return;
    }

    const invalidAddonBlock = blocks.find((block) => {
      const addonVariantId = (block.dataset.addonVariantId || '').toString().trim();
      if (!addonVariantId || addonVariantId !== String(mainVariantId)) return false;
      return this.hasBlockPersonalizationValue(block);
    });

    if (invalidAddonBlock) {
      const label = invalidAddonBlock.dataset.propertyLabel || invalidAddonBlock.dataset.personalizadorBlockKey || 'addon';
      this.toggleAddToCart(form, true);
      this.setError(
        wrapper,
        (wrapper.dataset.i18nErrorInvalidAddon || 'Configuracion invalida en "{label}": el addon no puede usar la misma variante que el producto principal.').replace('{label}', label)
      );
      this.emitDebug(wrapper);
      return;
    }

    const activeAddonBlocks = [];

    blocks.forEach((block) => {
      const addonVariantId = (block.dataset.addonVariantId || '').toString().trim();
      const blockKey = block.dataset.personalizadorBlockKey || 'personalizador';
      const hasZoneVariantIds =
        blockKey === 'logos' &&
        Array.from(block.querySelectorAll('[data-addon-variant-id]')).some(
          (el) => (el.dataset.addonVariantId || '').trim()
        );
      if (!addonVariantId && !hasZoneVariantIds) return;

      const visibleInput = this.getVisiblePropertyInput(block);
      const propertyLabel = block.dataset.propertyLabel || this.extractPropertyLabel(visibleInput?.name || '');
      if (blockKey === 'logos') {
        activeAddonBlocks.push(...this.buildLogoAddonBlocks(block, wrapper, addonVariantId, propertyLabel));
        return;
      }

      const visibleValue = this.resolveVisibleValue(block, visibleInput);
      const extraProperties = this.collectExtraProperties(block);
      if (!this.isMeaningfulValue(visibleValue) && extraProperties.length === 0) return;

      activeAddonBlocks.push({
        addonVariantId,
        propertyLabel,
        blockKey,
        visibleValue,
        extraProperties,
      });
    });

    if (activeAddonBlocks.length === 0) {
      this.emitDebug(wrapper);
      return;
    }

    this.appendHidden(form, 'properties[customized]', 'true');

    let addonIndex = 1;
    activeAddonBlocks.forEach((addon) => {
      const { addonVariantId, propertyLabel, blockKey, visibleValue, extraProperties } = addon;

      this.appendHidden(form, `items[${addonIndex}]id`, addonVariantId);
      this.appendHidden(form, `items[${addonIndex}]quantity`, String(safeQuantity));
      this.appendHidden(form, `items[${addonIndex}]parent_id`, String(mainVariantId));
      if (parentReference) {
        this.appendHidden(form, `items[${addonIndex}]properties[Producto base]`, parentReference);
      }

      this.appendHidden(form, `items[${addonIndex}]properties[customized]`, 'true');

      if (propertyLabel && this.isMeaningfulValue(visibleValue)) {
        this.appendHidden(form, `items[${addonIndex}]properties[${propertyLabel}]`, visibleValue);
      }

      this.appendHidden(form, `items[${addonIndex}]properties[_personalizador_block]`, blockKey);

      extraProperties.forEach((extra) => {
        this.appendHidden(form, `items[${addonIndex}]properties[${extra.key}]`, extra.value);
      });

      addonIndex += 1;
    });

    this.updateButtonPrice(wrapper, form);
    this.emitDebug(wrapper);
  }

  isRequiredAndEmpty(block) {
    if (!(block instanceof HTMLElement)) return false;

    const isRequired = block.dataset.required === 'true';
    if (!isRequired) return false;

    const visibleInput = this.getVisiblePropertyInput(block);
    if (!visibleInput) return true;

    return !this.isMeaningfulValue(visibleInput.value);
  }

  getVisiblePropertyInput(block) {
    const input = block.querySelector('[data-personalizador-visible-property]');
    return input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement || input instanceof HTMLSelectElement
      ? input
      : null;
  }

  resolveVisibleValue(block, visibleInput) {
    if (!(block instanceof HTMLElement)) return '';
    return visibleInput ? this.normalizePropertyValue(visibleInput.value) : '';
  }

  collectExtraProperties(block) {
    const extraProperties = [];
    const extraInputs = block.querySelectorAll('[data-personalizador-extra-property]');

    extraInputs.forEach((extraInput) => {
      if (
        !(
          extraInput instanceof HTMLInputElement ||
          extraInput instanceof HTMLTextAreaElement ||
          extraInput instanceof HTMLSelectElement
        )
      ) {
        return;
      }

      const value = this.normalizePropertyValue(extraInput.value);
      const key = (extraInput.dataset.extraKey || '').toString().trim();
      if (!this.isMeaningfulValue(value) || !key) return;

      extraProperties.push({ key, value });
    });

    return extraProperties;
  }

  buildLogoAddonBlocks(block, wrapper, addonVariantId, propertyLabel) {
    const selectedInputs = Array.from(
      block.querySelectorAll('.personalizador__logo-checkbox, .personalizador__logo-radio')
    ).filter((input) => input instanceof HTMLInputElement && input.checked);

    return selectedInputs.map((input) => {
      const positionValue = this.normalizePropertyValue(input.value);
      const positionLabel = this.normalizePropertyValue(input.dataset.label || positionValue);
      const extraProperties = this.collectLogoPositionProperties(wrapper, positionValue);
      const zoneVariantId = (input.dataset.addonVariantId || '').trim() || addonVariantId;

      if (!this.isMeaningfulValue(positionLabel) && extraProperties.length === 0) return null;

      return {
        addonVariantId: zoneVariantId,
        propertyLabel,
        blockKey: 'logos',
        visibleValue: positionLabel,
        extraProperties,
      };
    }).filter(Boolean);
  }

  collectLogoPositionProperties(wrapper, baseZone) {
    if (!(wrapper instanceof HTMLElement)) return [];

    const properties = [];
    const inputs = wrapper.querySelectorAll(
      '[data-logo-position-url], [data-logo-position-name]'
    );

    inputs.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;

      const row = input.closest('[data-logo-upload-row]');
      const key = this.extractPropertyLabel(input.getAttribute('name') || input.dataset.originalPropertyName || '');
      const value = this.normalizePropertyValue(input.value);

      if (row instanceof HTMLElement && row.dataset.uploadActive !== 'true') return;
      if (baseZone && row instanceof HTMLElement && row.dataset.baseZone !== baseZone) return;

      if (!key || !this.isMeaningfulValue(value)) return;

      properties.push({ key, value });
    });
    return this.dedupeProperties(properties);
  }

  dedupeProperties(properties) {
    const seen = new Set();
    return properties.filter((property) => {
      const signature = `${property.key}\u0000${property.value}`;
      if (seen.has(signature)) return false;
      seen.add(signature);
      return true;
    });
  }

  syncWrapperPropertyNames(wrapper, mode) {
    if (!(wrapper instanceof HTMLElement)) return;

    const inputs = wrapper.querySelectorAll(
      'input[name^="properties["], textarea[name^="properties["], select[name^="properties["]'
    );
    inputs.forEach((input) => this.syncInputNameByValue(input, mode));
  }

  syncParentPropertyNames(blocks, mode) {
    blocks.forEach((block) => {
      const inputs = block.querySelectorAll('[data-personalizador-visible-property], [data-personalizador-extra-property]');
      inputs.forEach((input) => this.syncInputNameByValue(input, mode));
    });
  }

  syncInputNameByValue(input, mode) {
    if (
      !(
        input instanceof HTMLInputElement ||
        input instanceof HTMLTextAreaElement ||
        input instanceof HTMLSelectElement
      )
    ) {
      return;
    }

    const currentName = input.getAttribute('name') || '';
    if (!input.dataset.originalPropertyName && currentName) {
      input.dataset.originalPropertyName = currentName;
    }

    const originalName = input.dataset.originalPropertyName || currentName;
    if (!originalName) return;

    if (mode !== 'custom') {
      if (input.hasAttribute('name')) {
        input.removeAttribute('name');
      }
      return;
    }

    if (this.isMeaningfulValue(input.value)) {
      if (input.getAttribute('name') !== originalName) {
        input.setAttribute('name', originalName);
      }
    } else if (input.hasAttribute('name')) {
      input.removeAttribute('name');
    }
  }

  hasBlockPersonalizationValue(block) {
    const visibleInput = this.getVisiblePropertyInput(block);
    const visibleValue = visibleInput ? this.normalizePropertyValue(visibleInput.value) : '';
    if (this.isMeaningfulValue(visibleValue)) return true;

    return this.collectExtraProperties(block).length > 0;
  }

  extractPropertyLabel(inputName) {
    if (!inputName) return '';
    const match = inputName.match(/properties\[(.+)]/);
    return match ? match[1] : '';
  }

  normalizePropertyValue(value) {
    return (value || '').toString().trim();
  }

  isMeaningfulValue(value) {
    const normalized = this.normalizePropertyValue(value);
    if (!normalized) return false;

    const lowered = normalized.toLowerCase();
    if (lowered === '-' || lowered === 'selecciona un pais' || lowered === 'selecciona una opcion') {
      return false;
    }

    return true;
  }

  getVariantTitleMap(wrapper) {
    if (!(wrapper instanceof HTMLElement)) return {};
    if (wrapper.__personalizadorVariantTitleMap) return wrapper.__personalizadorVariantTitleMap;

    const raw = wrapper.dataset.parentVariantMap || '';
    if (!raw) {
      wrapper.__personalizadorVariantTitleMap = {};
      return wrapper.__personalizadorVariantTitleMap;
    }

    try {
      const parsed = JSON.parse(raw);
      wrapper.__personalizadorVariantTitleMap = parsed && typeof parsed === 'object' ? parsed : {};
      return wrapper.__personalizadorVariantTitleMap;
    } catch (_error) {
      wrapper.__personalizadorVariantTitleMap = {};
      return wrapper.__personalizadorVariantTitleMap;
    }
  }

  buildParentReference(wrapper, mainVariantId) {
    if (!(wrapper instanceof HTMLElement) || !mainVariantId) return '';

    const productTitle = (wrapper.dataset.parentProductTitle || '').toString().trim();
    const map = this.getVariantTitleMap(wrapper);
    const variantTitleRaw = (map[String(mainVariantId)] || '').toString().trim();
    const variantTitle = variantTitleRaw.toLowerCase() === 'default title' ? '' : variantTitleRaw;

    if (!productTitle) return variantTitle;
    if (!variantTitle) return productTitle;
    return `${productTitle} - ${variantTitle}`;
  }

  getInputValue(root, selector) {
    const input = root.querySelector(selector);
    if (!(input instanceof HTMLInputElement)) return '';
    return input.value;
  }

  clearAddonInputs(form) {
    const existing = form.querySelectorAll('.personalizador-addon-input');
    existing.forEach((node) => node.remove());
  }

  appendHidden(form, name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    input.className = 'personalizador-addon-input';
    form.appendChild(input);
  }

  toggleAddToCart(form, disabled) {
    const button = form.querySelector('button[name="add"]');
    if (!(button instanceof HTMLButtonElement)) return;

    if (disabled) {
      button.disabled = true;
      button.dataset.personalizadorDisabled = 'true';
      return;
    }

    if (button.dataset.personalizadorDisabled === 'true') {
      button.disabled = false;
      delete button.dataset.personalizadorDisabled;
    }
  }

  setError(wrapper, message) {
    const errorNode = wrapper.querySelector('[data-personalizador-error]');
    if (!(errorNode instanceof HTMLElement)) return;

    if (!message) {
      errorNode.textContent = '';
      errorNode.classList.add('hidden');
      return;
    }

    errorNode.textContent = message;
    errorNode.classList.remove('hidden');
  }

  getVariantPriceMap(wrapper) {
    if (!(wrapper instanceof HTMLElement)) return {};
    const raw = wrapper.dataset.variantPrices || '';
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_error) {
      return {};
    }
  }

  getAddonPrices(wrapper) {
    if (!(wrapper instanceof HTMLElement)) return {};
    const raw = wrapper.dataset.addonPrices || '';
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_error) {
      return {};
    }
  }

  formatMoney(cents) {
    if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function') {
      const format = (window.Shopify.money_format || '{{amount}} €').replace('{{amount_no_decimals}}', '{{amount}}');
      return window.Shopify.formatMoney(cents, format);
    }
    const euros = (cents / 100).toFixed(2).replace('.', ',').replace(/,00$/, '');
    return euros + ' €';
  }

  injectPriceDisplay(form, wrapper) {
    if (!(form instanceof HTMLFormElement)) return null;
    let display = form.querySelector('[data-personalizador-price-display]');
    if (display instanceof HTMLElement) return display;

    const button = form.querySelector('button[name="add"]');
    if (!(button instanceof HTMLButtonElement)) return null;

    const buttonsContainer = button.closest('.product-form-buttons') || button.parentElement;
    const parent = buttonsContainer ? buttonsContainer.parentNode : button.parentNode;
    const insertBefore = buttonsContainer || button;

    display = document.createElement('p');
    display.dataset.personalizadorPriceDisplay = 'true';
    display.className = 'personalizador__price-display';
    display.hidden = true;

    if (parent) {
      parent.insertBefore(display, insertBefore);
    }

    const disclaimer = document.createElement('p');
    disclaimer.dataset.personalizadorDisclaimer = 'true';
    disclaimer.className = 'personalizador__disclaimer';
    disclaimer.hidden = true;
    if (parent) {
      parent.insertBefore(disclaimer, insertBefore);
    }

    return display;
  }

  calculateTotal(wrapper, form) {
    const mainVariantId = this.getInputValue(form, 'input[name="id"]');
    const variantPrices = this.getVariantPriceMap(wrapper);
    const addonPrices = this.getAddonPrices(wrapper);
    const basePrice = mainVariantId && variantPrices[String(mainVariantId)]
      ? Number(variantPrices[String(mainVariantId)])
      : 0;

    let addonTotal = 0;
    const blocks = Array.from(wrapper.querySelectorAll('.personalizador__block[data-personalizador-block-key]'));

    blocks.forEach((block) => {
      const blockKey = block.dataset.personalizadorBlockKey || '';

      if (blockKey === 'logos') {
        const checkedInputs = Array.from(block.querySelectorAll('.personalizador__logo-checkbox'))
          .filter((input) => input instanceof HTMLInputElement && input.checked);

        checkedInputs.forEach((input) => {
          const zonePrice = input.dataset.addonPrice ? Number(input.dataset.addonPrice) : 0;
          addonTotal += zonePrice > 0 ? zonePrice : (Number(addonPrices.logos) || 0);
        });
        return;
      }

      const visibleInput = this.getVisiblePropertyInput(block);
      const visibleValue = visibleInput ? this.normalizePropertyValue(visibleInput.value) : '';
      if (!this.isMeaningfulValue(visibleValue)) return;

      const price = Number(addonPrices[blockKey] || 0);
      addonTotal += price;
    });

    return basePrice + addonTotal;
  }

  updateButtonPrice(wrapper, form) {
    const display = this.injectPriceDisplay(form, wrapper);
    if (!(display instanceof HTMLElement)) return;

    const disclaimer = form.querySelector('[data-personalizador-disclaimer]');
    const mode = this.getMode(wrapper);

    if (mode !== 'custom') {
      display.hidden = true;
      display.textContent = '';
      if (disclaimer instanceof HTMLElement) disclaimer.hidden = true;
      return;
    }

    const total = this.calculateTotal(wrapper, form);
    if (total <= 0) {
      display.hidden = true;
      if (disclaimer instanceof HTMLElement) disclaimer.hidden = true;
      return;
    }

    display.hidden = false;
    const totalTemplate = wrapper.dataset.i18nEstimatedTotal || 'Total estimado: {price}';
    const [labelPart = '', afterPrice = ''] = totalTemplate.split('{price}');
    display.innerHTML = '<strong>' + labelPart + '</strong>' + this.formatMoney(total) + afterPrice;

    if (disclaimer instanceof HTMLElement) {
      const disclaimerText = wrapper.dataset.disclaimer || 'La previsualización es orientativa y puede no reflejar el resultado final del producto.';
      if (disclaimerText) {
        disclaimer.textContent = disclaimerText;
        disclaimer.hidden = false;
      } else {
        disclaimer.hidden = true;
      }
    }
  }

  emitDebug(wrapper) {
    if (!(wrapper instanceof HTMLElement)) return;
    wrapper.dispatchEvent(new CustomEvent('personalizador:sync-state', { bubbles: true }));
  }

  getMode(wrapper) {
    if (!(wrapper instanceof HTMLElement)) return 'none';
    return wrapper.dataset.personalizadorMode === 'custom' ? 'custom' : 'none';
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.personalizadorCartHandler = new PersonalizadorCartHandler();
});
